import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { createDataItem, fetchDataDomains, fetchDataType } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

const EMPTY_FORM = {
  dataItemName: "", dataItemDescription: "", url: "",
  primaryKey: false, foreignKey: false, referenceAttributeId: "",
  dataDomainId: "", dataDomainName: "",
};

export default function DataItemCreatePage() {
  const { businessDomainId, dataTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState(EMPTY_FORM);
  const [dataDomains, setDataDomains] = useState([]);
  const [dataTypeName, setDataTypeName] = useState("");

  const backPath = `/design/${businessDomainId}/data-types/${dataTypeId}`;

  useEffect(() => {
    (async () => {
      try {
        const domains = await fetchDataDomains(COMPANY_ID);
        setDataDomains(Array.isArray(domains) ? domains : []);
      } catch (e) { /* ignore */ }
      try {
        const dt = await fetchDataType(dataTypeId);
        setDataTypeName(dt.dataTypeName || "");
      } catch (e) { /* ignore */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.dataItemName) { notify.error(t("dataItem.name.required")); return; }
    const domain = dataDomains.find((d) => d.dataDomainId === form.dataDomainId);
    try {
      await createDataItem({
        ...form,
        companyId: COMPANY_ID,
        dataTypeId,
        dataTypeName,
        dataDomainName: domain?.dataDomainName || "",
      });
      notify.success(t("message.create.success"));
      navigate(backPath);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("dataItem.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required label={t("dataItem.name")}
              name="dataItemName" value={form.dataItemName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("dataItem.description")}
              name="dataItemDescription" value={form.dataItemDescription} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("dataItem.dataDomain")}</InputLabel>
              <Select name="dataDomainId" value={form.dataDomainId} label={t("dataItem.dataDomain")} onChange={handleChange}>
                <MenuItem value=""><em>—</em></MenuItem>
                {dataDomains.map((d) => (
                  <MenuItem key={d.dataDomainId} value={d.dataDomainId}>{d.dataDomainName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Checkbox name="primaryKey" checked={form.primaryKey} onChange={handleChange} />}
              label={t("dataItem.primaryKey")} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Checkbox name="foreignKey" checked={form.foreignKey} onChange={handleChange} />}
              label={t("dataItem.foreignKey")} />
          </Grid>
          {form.foreignKey && (
            <Grid size={12}>
              <TextField fullWidth label={t("dataItem.referenceAttributeId")}
                name="referenceAttributeId" value={form.referenceAttributeId} onChange={handleChange} />
            </Grid>
          )}
          <Grid size={12}>
            <TextField fullWidth type="url" label={t("common.url")}
              name="url" value={form.url} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(backPath)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
